// Featured Projects — asymmetric grid (desktop) / Swiper slider (mobile)
//
// Standalone bundle, enqueued only on pages that render the .featured-projects
// block (see blocks/featured-projects/functions.php), so Swiper never ships in
// the global app bundle.
//
// The cards render flat in one wrapper; the slider is driven differently per
// breakpoint:
//   • Desktop (≥783px): a custom pager shows one "set" of 3 cards at a time in
//     the asymmetric grid (one large left, two stacked right). Prev/next page
//     through sets and the counter reads `01 / 02`.
//   • Mobile (<783px): a peek-next Swiper pages one card at a time.
// Only one mechanism is ever live; switching breakpoint tears the other down so
// they never fight over the same DOM.

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

const DESKTOP_MIN = 783;
const DESKTOP = `(min-width: ${DESKTOP_MIN}px)`;
const PER_SET = 3;
const pad = (n) => String(n).padStart(2, '0');

const featuredProjects = {
  init: () => {
    document.querySelectorAll('[data-fp-swiper]').forEach(featuredProjects.setup);
  },

  setup: (el) => {
    // Carousel/standalone puts the controls inside the block's own
    // <section data-featured-projects>. The homepage grid pattern instead renders
    // the pager in its Gutenberg header (a cousin of the slider), so it marks the
    // surrounding group .featured-projects-scope and drops the section attribute —
    // root then resolves to that wrapper, which holds both the pager and slider.
    const root = el.closest('[data-featured-projects], .featured-projects-scope');
    if (!root) return;

    // Carousel mode (single-service / standalone): one full-width card at a
    // time, so the Swiper stays live on desktop and the set-pager is skipped.
    const isCarousel = root.hasAttribute('data-fp-carousel');

    const grid = el.querySelector('.featured-projects__grid');
    const cards = Array.from(el.querySelectorAll('.featured-projects__card'));
    const counter = root.querySelector('[data-fp-counter]');
    const nav = root.querySelector('.featured-projects__nav');
    const head = root.querySelector('.featured-projects__head--bare');
    const prevEl = root.querySelector('[data-fp-prev]');
    const nextEl = root.querySelector('[data-fp-next]');

    if (!grid || cards.length === 0) return;

    const setCounter = (current, total) => {
      if (counter) counter.textContent = `${pad(current)} / ${pad(total)}`;
    };

    // Show or hide the nav (and, for the pattern's bare head, the head itself)
    // depending on whether there is anything to page through at this breakpoint.
    // The bare head is desktop-hidden purely in CSS, so `headDisplay` says how to
    // reveal it when visible: the desktop pager forces 'flex' to override that CSS
    // (otherwise the nested nav stays hidden under a display:none parent), while
    // the mobile Swiper passes '' to defer to the media query (flex on mobile).
    // When hidden, both collapse to 'none'.
    const showControls = (visible, headDisplay = '') => {
      if (nav) nav.style.display = visible ? 'flex' : 'none';
      if (head) head.style.display = visible ? headDisplay : 'none';
    };

    // Desktop set-pager -------------------------------------------------------
    const SHIFT = 56; // Horizontal travel of the slide-in/out, in px.
    const OUT_MS = 170;
    const IN_MS = 240;
    const sets = Math.ceil(cards.length / PER_SET);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
    let setIndex = 0;
    let animating = false;

    const activeCards = () => cards.filter((card) => card.classList.contains('is-active'));

    const renderSet = () => {
      grid.classList.add('is-paged');
      cards.forEach((card, i) => {
        const inSet = Math.floor(i / PER_SET) === setIndex;
        card.classList.toggle('is-active', inSet);
        card.classList.toggle('is-lead', inSet && i % PER_SET === 0);
      });
      setCounter(setIndex + 1, sets);
      if (prevEl) prevEl.classList.toggle('swiper-button-disabled', setIndex === 0);
      if (nextEl) nextEl.classList.toggle('swiper-button-disabled', setIndex === sets - 1);
    };

    // Slide the current set out (dir · left for next, right for prev), swap, then
    // slide the incoming set in from the opposite edge. Input is ignored while a
    // transition runs so rapid clicks never overlap.
    const goToSet = (index) => {
      if (animating || index === setIndex || index < 0 || index > sets - 1) return;

      const dir = index > setIndex ? 1 : -1;

      if (!canAnimate || reduceMotion.matches) {
        setIndex = index;
        renderSet();
        return;
      }

      animating = true;

      const out = activeCards().map((card) => card.animate(
        [{ transform: 'translateX(0)', opacity: 1 }, { transform: `translateX(${-dir * SHIFT}px)`, opacity: 0 }],
        { duration: OUT_MS, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      ));

      Promise.all(out.map((a) => a.finished)).then(() => {
        setIndex = index;
        renderSet();
        // Clear the forwards-fill so the leaving cards are clean when reused.
        out.forEach((a) => a.cancel());
        const incoming = activeCards().map((card, i) => card.animate(
          [{ transform: `translateX(${dir * SHIFT}px)`, opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }],
          { duration: IN_MS, delay: i * 40, easing: 'cubic-bezier(0, 0, 0.2, 1)', fill: 'backwards' }
        ));
        return Promise.all(incoming.map((a) => a.finished));
      }).then(() => {
        animating = false;
      }).catch(() => {
        // A cancelled animation (breakpoint teardown) lands here — just settle.
        animating = false;
      });
    };

    const onPrev = () => goToSet(setIndex - 1);
    const onNext = () => goToSet(setIndex + 1);

    const enablePager = () => {
      showControls(sets > 1, 'flex');
      renderSet();
      if (prevEl) prevEl.addEventListener('click', onPrev);
      if (nextEl) nextEl.addEventListener('click', onNext);
    };

    const disablePager = () => {
      if (prevEl) prevEl.removeEventListener('click', onPrev);
      if (nextEl) nextEl.removeEventListener('click', onNext);
      cards.forEach((card) => {
        if (card.getAnimations) card.getAnimations().forEach((a) => a.cancel());
        card.classList.remove('is-active', 'is-lead');
      });
      animating = false;
      grid.classList.remove('is-paged');
    };

    // Mobile Swiper -----------------------------------------------------------
    let swiper = null;

    const updateSwiperCounter = ({ activeIndex, slides }) => setCounter(activeIndex + 1, slides.length);

    const enableSwiper = () => {
      showControls(cards.length > 1);
      if (swiper) return;
      swiper = new Swiper(el, {
        modules: [Navigation],
        slidesPerView: 'auto',
        spaceBetween: 16,
        grabCursor: true,
        navigation: { prevEl, nextEl },
        // Carousel mode keeps the slider on desktop, where the gap is wider.
        ...(isCarousel
          ? { breakpoints: { [DESKTOP_MIN]: { spaceBetween: 32 } } }
          : {}),
        on: {
          init: updateSwiperCounter,
          slideChange: updateSwiperCounter,
        },
      });
    };

    const disableSwiper = () => {
      if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }
      // Reset the head's inline display so the mobile media query can hide it.
      if (head) head.style.display = '';
    };

    // Breakpoint switch -------------------------------------------------------
    const mq = window.matchMedia(DESKTOP);

    const sync = () => {
      // Carousel mode: one Swiper for every breakpoint (spaceBetween adapts via
      // its own breakpoints), so the desktop set-pager never runs.
      if (isCarousel) {
        enableSwiper();
        return;
      }
      if (mq.matches) {
        disableSwiper();
        enablePager();
      } else {
        disablePager();
        enableSwiper();
      }
    };

    sync();
    if (!isCarousel) mq.addEventListener('change', sync);
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', featuredProjects.init);
} else {
  featuredProjects.init();
}

export default featuredProjects;
